/**
 * PasswordGate Component
 * Full-screen password prompt shown when ARTEMIS_MODE_ACTIVE is false.
 * Reads the correct password from import.meta.env.VITE_PASSWORD.
 * On success, unlocks for the duration of the browser session (sessionStorage).
 * If ARTEMIS_MODE_ACTIVE is true, renders children immediately with no gate.
 */

import { useState, useCallback } from 'react'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { useArtemisMode } from '../artemis/ArtemisModeContext'
import { parseHighlights } from './Subheading'

const SESSION_KEY = 'dsl_unlocked'

function isSessionUnlocked() {
  try {
    return sessionStorage.getItem(SESSION_KEY) === 'true'
  } catch {
    return false
  }
}

function setSessionUnlocked() {
  try {
    sessionStorage.setItem(SESSION_KEY, 'true')
  } catch {
    // sessionStorage unavailable — fail gracefully, just allow through
  }
}

export function PasswordGate({ children }) {
  const { isArtemisModeActive } = useArtemisMode()

  const [unlocked, setUnlocked] = useState(() => isSessionUnlocked())
  const [input, setInput] = useState('')
  const [error, setError] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const toggleShowPassword = useCallback(() => setShowPassword(v => !v), [])

  const handleSubmit = useCallback((e) => {
    e.preventDefault()

    const correctPassword = import.meta.env.VITE_PASSWORD

    // Fail closed — if no password is configured, never grant access
    if (!correctPassword) {
      setError(true)
      setInput('')
      return
    }

    if (input === correctPassword) {
      setSessionUnlocked()
      setUnlocked(true)
    } else {
      setError(true)
      setInput('')
    }
  }, [input])

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value)
    if (error) setError(false)
  }, [error])

  // Artemis mode active — no gate needed
  if (isArtemisModeActive) return children

  // Already unlocked this session
  if (unlocked) return children

  return (
    <div className="password-gate">
      <div className="password-gate__panel">
        <img
          src="/logos/dsl-stacked.svg"
          alt="Dark Star Labs"
          className="password-gate__logo"
          onError={(e) => { e.target.style.display = 'none' }}
        />
        <p className="password-gate__notice">
          {parseHighlights('The following is an actively in development portfolio. *Features and functionality may change or become inaccessible* during development phases.')}
          <br /><br />
          Access is restricted to approved partners and customers. If you believe you require access please contact <a className="password-gate__contact" href="mailto:engage@darkstarlabs.io">engage@darkstarlabs.io</a>
        </p>
        <form className="password-gate__form" onSubmit={handleSubmit}>
          <div className="password-gate__input-wrapper">
            <input
              className={`password-gate__input${error ? ' password-gate__input--error' : ''}`}
              type={showPassword ? 'text' : 'password'}
              value={input}
              onChange={handleInputChange}
              placeholder="ENTER PASSWORD"
              autoComplete="current-password"
              autoFocus
              spellCheck={false}
            />
            <button
              type="button"
              className="password-gate__reveal"
              onClick={toggleShowPassword}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          {error && (
            <p className="password-gate__error">INCORRECT PASSWORD</p>
          )}
          <button className="password-gate__button" type="submit">
            ENTER
          </button>
        </form>
        <div className="password-gate__socials">
          <a className="password-gate__social-link" href="https://www.linkedin.com/company/darkstarlabs" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
            <img src="/icons/LinkedIn-Red.png" alt="LinkedIn" />
          </a>
          <a className="password-gate__social-link" href="https://www.instagram.com/darkstarlabs.io/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <img src="/icons/Instagram-Red.png" alt="Instagram" />
          </a>
          <a className="password-gate__social-link" href="https://www.youtube.com/@DarkStarLabs" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
            <img src="/icons/YouTube-Red.png" alt="YouTube" />
          </a>
          <a className="password-gate__social-link" href="https://darkstarlabs.io" target="_blank" rel="noopener noreferrer" aria-label="Website">
            <img src="/icons/web_red.png" alt="Website" />
          </a>
        </div>
      </div>
    </div>
  )
}
